import { s3PublicStoragePrefix } from '../../../../utility'
import unmuteIcon from '../../../../assets/image/video/unmuteIcon.svg'
import playIcon from '../../../../assets/image/video/playIcon.svg'
import { useEffect, useRef, useState } from 'react'
import { Gender } from '../../../hooks/useCubeItems/kandi/wahlkreisData'
import Loading from '../../../UI/Loading/Loading'
import close from '../../../../assets/image/video/close.svg'

type Props = {
  skip?: () => void
  gender?: Gender
  isTopic?: boolean
}

const Video: React.FC<Props> = ({ skip, gender = null, isTopic = false }) => {
  const desktopRef = useRef<HTMLVideoElement>(null)
  const mobileRef = useRef<HTMLVideoElement>(null)
  const [isMuted, setIsMuted] = useState(true)
  const [isPaused, setIsPaused] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [initStartDone, setInitStartDone] = useState(false)

  const getVideo = (size: 'mobile' | 'desktop') => {
    const links = {
      w: {
        mobile: `${s3PublicStoragePrefix}custom-static-assets/videos/Aufsager/9x16/250109_SPD_Cube_Aufsager_Kandidatin_9x16__FINAL_subbed_optimized.mp4`,
        desktop: `${s3PublicStoragePrefix}custom-static-assets/videos/Aufsager/16x9/250109_SPD_Cube_Aufsager_Kandidatin_16x9__FINAL_subbed_optimized.mp4`,
      },
      m: {
        mobile: `${s3PublicStoragePrefix}custom-static-assets/videos/Aufsager/9x16/250109_SPD_Cube_Aufsager_Kandidat_9x16__FINAL_subbed_optimized.mp4`,
        desktop: `${s3PublicStoragePrefix}custom-static-assets/videos/Aufsager/16x9/250109_SPD_Cube_Aufsager_Kandidat_16x9__FINAL_subbed_optimized.mp4`,
      },
      default: {
        mobile: `${s3PublicStoragePrefix}custom-static-assets/videos/Aufsager/9x16/250109_SPD_Cube_Aufsager_Generell_9x16__FINAL_subbed_optimized.mp4`,
        desktop: `${s3PublicStoragePrefix}custom-static-assets/videos/Aufsager/16x9/250109_SPD_Cube_Aufsager_Generell_16x9__FINAL_subbed_optimized.mp4`,
      },
      topic: {
        mobile: `${s3PublicStoragePrefix}custom-static-assets/videos/Aufsager/9x16/250109_SPD_Cube_Aufsager_Programm_9x16__FINAL_subbed_optimized.mp4`,
        desktop: `${s3PublicStoragePrefix}custom-static-assets/videos/Aufsager/16x9/250109_SPD_Cube_Aufsager_Programm_16x9__FINAL_subbed_optimized.mp4`,
      },
    }
    if (!!isTopic) {
      return links.topic[size]
    } else if (!!gender) {
      return links[gender][size]
    }
    return links.default[size]
  }

  const getVideoInstance = () => {
    const windowWidth = window.innerWidth
    if (windowWidth < 768) {
      return mobileRef.current
    }
    return desktopRef.current
  }

  const toggle = () => {
    const video = getVideoInstance()
    if (video && !showLoading()) {
      if (isMuted) {
        video.muted = false
        setIsMuted(false)
        setIsLoading(false)
      } else if (!isMuted && isPaused) {
        video.play()
      } else {
        video.pause()
      }
    }
  }

  useEffect(() => {
    if (!initStartDone) {
      const timeout = setTimeout(() => {
        const video = getVideoInstance()
        if (video) {
          setInitStartDone(true)
          video.play()
        }
      }, 1000)

      return () => clearTimeout(timeout)
    }
  }, [initStartDone])

  const onVideoStop = () => {
    setIsPaused(true)
    setIsLoading(false)
  }

  const onVideoPlay = () => {
    setIsPaused(false)
    setIsLoading(false)
  }

  const onVideoLoad = () => {
    setIsLoading(true)
  }

  const onVideoCanPlay = () => {
    setIsLoading(false)
  }

  const showLoading = () => {
    const video = getVideoInstance()
    if (video) {
      if (video.readyState < 4) {
        return isLoading
      } else {
        return false
      }
    }
    return isLoading
  }

  const callSkip = () => {
    if (!!skip) {
      skip()
    }
  }

  return (
    <div className="shadow-lg relative">
      {/* MOBILE */}
      <video
        ref={mobileRef}
        src={getVideo('mobile')}
        className="block md:hidden w-full"
        controls={false}
        autoPlay={true}
        playsInline
        muted
        onClick={toggle}
        onEnded={callSkip}
        onPause={onVideoStop}
        onPlay={onVideoPlay}
        onPlaying={onVideoPlay}
        onCanPlay={onVideoCanPlay}
        onWaiting={onVideoLoad}
        onStalled={onVideoLoad}
      />
      {/* DESKTOP */}
      <video
        ref={desktopRef}
        src={getVideo('desktop')}
        className="hidden md:block w-full"
        controls={false}
        autoPlay={true}
        playsInline
        muted
        onClick={toggle}
        onEnded={callSkip}
        onPause={onVideoStop}
        onPlay={onVideoPlay}
        onPlaying={onVideoPlay}
        onCanPlay={onVideoCanPlay}
        onWaiting={onVideoLoad}
        onStalled={onVideoLoad}
      />
      {isMuted && !showLoading() && (
        <div className="pointer-events-none absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <img src={unmuteIcon} alt="Play Button" className="w-20" />
        </div>
      )}
      {isPaused && (
        <div className="pointer-events-none absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <img src={playIcon} alt="Play Button" className="w-20" />
        </div>
      )}
      {showLoading() && (
        <div className="pointer-events-none absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <Loading loading={false} />
        </div>
      )}
      {!!skip && (
        <div className="cursor-pointer absolute top-[10px] right-[10px]">
          <img onClick={callSkip} src={close} className="w-6 h-6" alt="" />
        </div>
      )}
    </div>
  )
}

export default Video
